import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronRight, Clock, ArrowRight } from "lucide-react";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import { BLOG_POSTS } from "../data/siteData";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={`${post.title} | Complisure Counsultancy`}
        description={post.excerpt}
        canonical={`https://compliance-capital-1.preview.emergentagent.com/blog/${post.slug}`}
        jsonLd={[
          orgJsonLd,
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            datePublished: post.date,
            author: { "@type": "Organization", name: "Complisure Counsultancy" },
          },
        ]}
      />
      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="blob blob-float left-[-8%] top-[-8%] h-96 w-96 bg-brand-blue/10" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
          <Reveal>
            <nav className="flex items-center gap-1.5 text-xs text-brand-gray" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-brand-blue">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/blog" className="hover:text-brand-blue">Resources</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-brand-navy">{post.category}</span>
            </nav>
            <div className="mt-6 flex items-center gap-3">
              <span className="rounded-full bg-brand-ice px-3 py-1 text-xs font-semibold text-brand-blue">{post.category}</span>
              <span className="flex items-center gap-1 text-xs text-brand-gray"><Clock className="h-3 w-3" /> {post.readTime} read</span>
              <span className="text-xs text-brand-gray">{new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
            </div>
            <h1 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-[-0.02em] text-brand-navy sm:text-4xl">{post.title}</h1>
          </Reveal>

          <Reveal delay={0.1} className="mt-10">
            <article className="space-y-8">
              {post.content.map((block, i) => (
                <section key={i}>
                  <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">{block.h}</h2>
                  <p className="mt-3 text-base leading-relaxed text-brand-gray">{block.p}</p>
                </section>
              ))}
            </article>
          </Reveal>

          <Reveal className="mt-14">
            <div className="glass rounded-3xl p-7">
              <h2 className="font-display text-xl font-bold text-brand-navy">Facing this decision right now?</h2>
              <p className="mt-2 text-sm text-brand-gray">Get a senior advisor's read on your specific situation — free, confidential, one call.</p>
              <Link to="/contact" data-testid="blog-post-cta" className="btn-gradient mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white">
                Book Free Consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          <div className="mt-14">
            <h2 className="font-display text-xl font-bold text-brand-navy">Keep reading</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {others.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="glass card-lift group rounded-2xl p-5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-blue">{p.category}</span>
                  <p className="mt-2 text-sm font-semibold leading-snug text-brand-navy group-hover:text-brand-blue">{p.title}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
